/**
 * Service to handle video credit operations
 */

import { ENV } from '../config/env';
import { supabase } from '../lib/supabase';

// API base URL from environment configuration
const API_BASE_URL = `${ENV.API_URL}/api`;

export interface CreditResponse {
  success: boolean;
  credits?: number;
  error?: string;
}

export interface Transaction {
  id: string;
  user_id: string;
  amount: number;
  balance_after: number;
  description: string;
  transaction_type: string;
  reference_id?: string;
  created_at: string;
}

export interface TransactionHistoryResponse {
  success: boolean;
  transactions?: Transaction[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
  error?: string;
}

export interface AddCreditsResponse {
  success: boolean;
  transactionId?: string;
  credits?: number;
  message?: string;
  error?: string;
}

// Retrieve the current Supabase access token for Authorization header
const getToken = async (): Promise<string> => {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token || '';
};

/**
 * Get current user's video credit balance
 * 
 * @returns Credit balance or error
 */
export const getUserVideoCredits = async (userId?: string): Promise<CreditResponse> => {
  try {
    // Backend derives the user from verifyToken; do not pass userId in query
    const url = `${API_BASE_URL}/users/video-credits`;
    const token = await getToken();
    if (!token) throw new Error('Not authenticated');
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      credentials: 'include'
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `Error ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    return {
      success: true,
      credits: data.credits || 0
    };
  } catch (error) {
    console.error('Error fetching video credits:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch video credits'
    };
  }
};

/**
 * Get user's video credit transaction history
 * 
 * @param page Page number (default: 1)
 * @param limit Results per page (default: 10)
 * @returns Transaction history or error
 */
export const getVideoTransactionHistory = async (page = 1, limit = 10): Promise<TransactionHistoryResponse> => {
  try {
    const token = await getToken();
    if (!token) throw new Error('Not authenticated');
    const response = await fetch(
      `${API_BASE_URL}/users/video-credits/history?page=${page}&limit=${limit}`, 
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `Error ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    return {
      success: true,
      transactions: data.transactions || [],
      pagination: data.pagination
    };
  } catch (error) {
    console.error('Error fetching transaction history:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch transaction history'
    };
  }
};

/**
 * Add credits to a user's account (admin only)
 * 
 * @param userId User ID to add credits to
 * @param amount Amount of credits to add
 * @param description Optional description of the transaction
 * @returns Response with new balance or error
 */
export const addUserVideoCredits = async (
  userId: string, 
  amount: number, 
  description?: string
): Promise<AddCreditsResponse> => {
  try {
    if (!userId || amount <= 0) {
      throw new Error('Invalid parameters: userId and positive amount required');
    }

    const token = await getToken();
    if (!token) throw new Error('Not authenticated');
    const response = await fetch(`${API_BASE_URL}/admin/video-credits/add`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      credentials: 'include',
      body: JSON.stringify({
        userId,
        amount,
        description: description || `Added ${amount} credits`
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `Error ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    return {
      success: true,
      transactionId: data.transactionId,
      credits: data.credits,
      message: data.message
    };
  } catch (error) {
    console.error('Error adding video credits:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to add video credits'
    };
  }
};

/**
 * Check if current user is an administrator
 * 
 * @returns Boolean indicating if user is admin or error
 */
export const checkAdminStatus = async (): Promise<{success: boolean; isAdmin?: boolean; error?: string}> => {
  try {
    const response = await fetch(`${API_BASE_URL}/users/check-admin`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include'
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `Error ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    return {
      success: true,
      isAdmin: data.isAdmin
    };
  } catch (error) {
    console.error('Error checking admin status:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to check admin status'
    };
  }
};

/**
 * Get list of users (admin only)
 * 
 * @param page Page number (default: 1)
 * @param limit Results per page (default: 50)
 * @returns List of users or error
 */
export const getUsers = async (page = 1, limit = 50): Promise<{success: boolean; users?: any[]; error?: string}> => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/admin/users?page=${page}&limit=${limit}`, 
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `Error ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    return {
      success: true,
      users: data.users || []
    };
  } catch (error) {
    console.error('Error fetching users:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch users'
    };
  }
};
